import { Container, FillGradient, Graphics } from 'pixi.js';
import type { SetDefinition } from '../../data/types';
import { type TentPalette, tentAt } from '../../shared/full/palette';
import type { Vec2 } from '../../shared/vec';
import type { SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import { decorSet, type Frame } from '../frame';
import { parseHexColor } from '../palette';
import { setTint, releaseDrawing } from '../util';
import { khatam } from './desert-layout';
import {
  EDGE_WIDTH,
  LANTERN_SIZE,
  PEG,
  POUF_RADIUS,
  type Segment,
  type TentLayout,
  creatureLayout,
  tentLayout,
} from './pumpui-layout';

const WHITE = 0xffffff;
const DARK_WOOD = 0x2a1a0c;
const PEAK_RADIUS = 200;
// The cloth's five glows are baked once into one texture, coarse under the red edge hiding their rim.
const PEAK_RESOLUTION = 0.5;
const POOL_RADIUS = LANTERN_SIZE * 5.5;
const STARS_OVER_POOL = 0.3 / 0.22;
const DENSE_WOOD = 0.2;
const POUF_SPOKE = POUF_RADIUS * (2 / 3);

type Color = {
  [K in keyof TentPalette]: TentPalette[K] extends string ? K : never;
}[keyof TentPalette];

function lines(shape: Graphics, segments: readonly Segment[]): Graphics {
  for (const { from, to } of segments) {
    shape.moveTo(from.x, from.y).lineTo(to.x, to.y);
  }
  return shape;
}

function scalloped(shape: Graphics, { anchors, scallops }: TentLayout): Graphics {
  const [first] = anchors;
  if (first === undefined) {
    return shape;
  }
  shape.moveTo(first.x, first.y);
  for (const { control, to } of scallops) {
    shape.quadraticCurveTo(control.x, control.y, to.x, to.y);
  }
  return shape.closePath();
}

function flat(points: readonly Vec2[]): number[] {
  return points.flatMap(({ x, y }) => [x, y]);
}

function fading(center: Vec2, radius: number): FillGradient {
  return new FillGradient({
    type: 'radial',
    center,
    innerRadius: 0,
    outerCenter: center,
    outerRadius: radius,
    colorStops: [
      { offset: 0, color: 'rgba(255, 255, 255, 1)' },
      { offset: 1, color: 'rgba(255, 255, 255, 0)' },
    ],
    textureSpace: 'global',
  });
}

export function createPumpui(ctx: RenderContext): Family {
  const root = new Container();
  ctx.layers.decor.addChild(root);
  const tinted: [Container, Color][] = [];
  function shape(color: Color, parent: Container = root): Graphics {
    const graphics = new Graphics();
    parent.addChild(graphics);
    tinted.push([graphics, color]);
    return graphics;
  }
  const creature = new Container();
  root.addChild(creature);
  const wood = shape('bois', creature);
  const denseWood = shape('bois', creature);
  const woodLines = shape('boisTrait', creature);
  const ropes = shape('corde');
  const pegs = shape('piquet');
  const cloth = shape('toile');
  const peakLayer = new Container();
  root.addChild(peakLayer);
  tinted.push([peakLayer, 'pic']);
  const peaks = new Graphics();
  peakLayer.addChild(peaks);
  const stripes = shape('bande');
  const seams = shape('couture');
  const edge = shape('bord');
  const indigo = shape('indigo');
  const cream = shape('creme');
  const pools = shape('lueur');
  const projections = shape('lueur');
  const brass = shape('laiton');
  const glass = shape('verre');
  const flames = shape('flamme');
  const masts = shape('laiton');
  const poufs = shape('pouf');
  const poufLines = shape('poufTrait');
  let gradients: FillGradient[] = [];

  let drawnKey = '';
  let styledAt = Number.NaN;

  function drawCreature(center: Vec2): void {
    const layout = creatureLayout(center);
    creature.position.set(layout.at.x, layout.at.y);
    creature.rotation = layout.rotation;
    creature.scale.set(layout.scale);

    wood.clear();
    woodLines.clear();
    for (const wing of layout.wings) {
      wood.poly(flat(wing)).fill(WHITE);
      woodLines.poly(flat(wing)).stroke({ width: 4, color: WHITE, join: 'round', cap: 'round' });
    }
    wood.poly(flat(layout.tail)).fill(WHITE);
    lines(woodLines, layout.ribs).stroke({ width: 2.4, color: WHITE, cap: 'round' });
    lines(woodLines, layout.rungs).stroke({ width: 1.6, color: WHITE, alpha: 0.85, cap: 'round' });
    lines(woodLines, layout.braces).stroke({ width: 1.1, color: WHITE, alpha: 0.6, cap: 'round' });
    woodLines.poly(flat(layout.tail)).stroke({ width: 3, color: WHITE, join: 'round' });
    lines(woodLines, layout.tailRibs).stroke({ width: 1.6, color: WHITE, cap: 'round' });

    denseWood.clear();
    const [from, one, two, to] = layout.neck;
    denseWood
      .moveTo(from.x, from.y)
      .bezierCurveTo(one.x, one.y, two.x, two.y, to.x, to.y)
      .stroke({ width: 20, color: WHITE, cap: 'round' });
    lines(woodLines, layout.neckDashes).stroke({ width: 3, color: WHITE });
    denseWood.ellipse(0, 0, layout.body.rx, layout.body.ry).fill(WHITE);
    woodLines.ellipse(0, 0, layout.body.rx, layout.body.ry).stroke({ width: 4, color: WHITE });
    lines(woodLines, layout.bodyLines).stroke({
      width: 1.6,
      color: WHITE,
      alpha: 0.7,
      cap: 'round',
    });
    denseWood.poly(flat(layout.head)).fill(WHITE);
    woodLines.poly(flat(layout.head)).stroke({ width: 3.5, color: WHITE, join: 'round' });
    for (const eye of layout.eyes) {
      woodLines.circle(eye.x, eye.y, 3.5).fill(WHITE);
    }
  }

  function drawTent(tent: TentLayout, mastsOfSet: readonly Vec2[]): void {
    for (const graphics of [ropes, pegs, cloth, peaks, stripes, seams, edge, indigo, cream]) {
      graphics.clear();
    }
    lines(ropes, tent.ropes).stroke({ width: 1.5, color: WHITE });
    for (const { to } of tent.ropes) {
      pegs.rect(to.x - PEG / 2, to.y - PEG / 2, PEG, PEG);
    }
    pegs.fill(WHITE);

    scalloped(cloth, tent).fill(WHITE);
    for (const mast of mastsOfSet) {
      const gradient = fading(mast, PEAK_RADIUS);
      gradients.push(gradient);
      scalloped(peaks, tent).fill(gradient);
    }
    peakLayer.cacheAsTexture({ resolution: PEAK_RESOLUTION });
    peakLayer.updateCacheTexture();
    lines(stripes, tent.seams).stroke({ width: 22, color: WHITE });
    lines(seams, tent.seams).stroke({ width: 1.4, color: WHITE, alpha: 0.45 });
    scalloped(edge, tent).stroke({ width: EDGE_WIDTH, color: WHITE, alpha: 0.9, miterLimit: 4 });
    for (const { at, angle, cream: light } of tent.triangles) {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const corner = (x: number, y: number) => [at.x + x * cos - y * sin, at.y + x * sin + y * cos];
      (light ? cream : indigo)
        .poly([...corner(-7, 4), ...corner(0, -6), ...corner(7, 4)])
        .fill(WHITE);
    }
  }

  function drawLanterns(tent: TentLayout): void {
    for (const graphics of [pools, projections, brass, glass, flames]) {
      graphics.clear();
    }
    for (const { center, stars } of tent.lanterns) {
      const gradient = fading(center, POOL_RADIUS);
      gradients.push(gradient);
      pools.circle(center.x, center.y, POOL_RADIUS).fill(gradient);
      for (const star of stars) {
        projections.poly(khatam(star, LANTERN_SIZE * 0.42)).fill(WHITE);
      }
      brass
        .poly(khatam(center, LANTERN_SIZE))
        .fill({ color: WHITE, alpha: 0.85 })
        .stroke({ width: 2, color: WHITE, join: 'round' });
      glass
        .poly(khatam(center, LANTERN_SIZE * 0.55, Math.PI / 8))
        .fill({ color: WHITE, alpha: 0.9 });
      flames.circle(center.x, center.y, LANTERN_SIZE * 0.18).fill(WHITE);
    }
  }

  function draw(set: SetDefinition): void {
    const center = { x: set.arena.width / 2, y: set.arena.height / 2 };
    const mastsOfSet = set.obstacles ?? [];
    const tent = tentLayout(center, mastsOfSet);
    const old = gradients;
    gradients = [];
    drawCreature(center);
    drawTent(tent, mastsOfSet);
    drawLanterns(tent);
    for (const gradient of old) {
      gradient.destroy();
    }

    masts.clear();
    for (const mast of mastsOfSet) {
      masts.circle(mast.x, mast.y, mast.radius - 1.5).fill({ color: WHITE, alpha: 0.45 });
      masts.circle(mast.x, mast.y, mast.radius - 1.5).stroke({ width: 4, color: WHITE });
      masts.circle(mast.x, mast.y, mast.radius).stroke({ width: 1, color: DARK_WOOD, alpha: 0.5 });
      masts.circle(mast.x, mast.y, mast.radius * 0.35).fill(WHITE);
    }

    poufs.clear();
    poufLines.clear();
    for (const pouf of tent.poufs) {
      poufs.circle(pouf.x, pouf.y, POUF_RADIUS).fill(WHITE);
      poufLines.circle(pouf.x, pouf.y, POUF_RADIUS).stroke({ width: 2, color: WHITE });
      for (let spoke = 0; spoke < 4; spoke += 1) {
        const angle = (spoke * Math.PI) / 4;
        const arm = { x: Math.cos(angle) * POUF_SPOKE, y: Math.sin(angle) * POUF_SPOKE };
        poufLines.moveTo(pouf.x - arm.x, pouf.y - arm.y).lineTo(pouf.x + arm.x, pouf.y + arm.y);
      }
      poufLines.stroke({ width: 1.4, color: WHITE, alpha: 0.8 });
    }
  }

  function style(frame: Frame): void {
    styledAt = frame.fraction;
    const tent = tentAt(frame.fraction);
    for (const [graphics, token] of tinted) {
      setTint(graphics, parseHexColor(tent[token]));
    }
    wood.alpha = tent.boisAlpha;
    denseWood.alpha = tent.boisAlpha + DENSE_WOOD;
    cloth.alpha = tent.toileAlpha;
    peakLayer.alpha = tent.picAlpha;
    stripes.alpha = tent.bandeAlpha;
    pools.alpha = tent.lueurAlpha;
    projections.alpha = tent.lueurAlpha * STARS_OVER_POOL;
    pools.visible = projections.visible = tent.lueurAlpha > 0;
    const mode = frame.light.additive ? 'add' : 'normal';
    pools.blendMode = mode;
    projections.blendMode = mode;
  }

  return {
    update(_state: SimState, _alpha: number, frame: Frame): void {
      const set = decorSet(frame, 'pumpui');
      if (set !== undefined && set.id !== drawnKey) {
        drawnKey = set.id;
        draw(set);
      } else if (set === undefined && drawnKey !== '') {
        drawnKey = '';
        releaseDrawing(root);
      }
      root.visible = frame.set.decor === 'pumpui';
      if (!root.visible) {
        return;
      }
      if (frame.fraction !== styledAt) {
        style(frame);
      }
    },
    destroy(): void {
      root.destroy({ children: true });
      for (const gradient of gradients) {
        gradient.destroy();
      }
    },
  };
}
